/**
 * Created by fers on 26.09.15.
 */

import { v4 as uuidv4 } from 'uuid';
import { declareLinkedEntity } from '../fw/MetaDataHelper.ts';
import * as Permission from '../util/Permission';

export const operationTypes = {
    "view": {
        name: "View",
    },
    "list": {
        name: "List",
    },
    "edit": {
        name: "Edit",
    }
};

export const WORKING_STATUS_OK = "ok";
export const WORKING_STATUS_CACHING = "caching";
export const WORKING_STATUS_NO_ACTION = "no-action";

export default declareLinkedEntity({
    entity: "channelMappingDefs",
    graphQL: "ChannelMappingDef",
    name: "Channel Mapping Definition",
    defaultOrder: ['order', 'channelDef_id', 'mapping'],
    writePermission: [
        Permission.PERM_EDIT_CHANNEL_DEF
    ],
    dynamoPrefix: 'def',
    storageType: 'schema',
    parentRelation: 'channelMappingDefs',
    defaultTitle: (data) => {
        return data['mapping'];
    },
    onModify: ['channelDef'],
    fields: (types) => { return {
        id: {
            name: "Application Mapping Definition ID",
            type: "id",
            defaultValue: () => {
                return uuidv4();
            },
            primaryKey: true,
            required: true,
            userEditable: false,
        },
        channelDef: {
            name: "Application Definition",
            to: () => types['channelDefs'],
            type: "relation",
            defaultValue: (channelDef) => {
                return channelDef['id'];
            },
            required: true,
            parent: true,
            compositeKey: true,
            hiddenCreate: true,
        },
        channelDefVersion: {
            name: "Channel Definition Version",
            to: () => types['channelDefVersions'],
            type: "relation",
            defaultValue: (channelDef) => {
                return channelDef['channelDefVersion_id'];
            },
            required: true,
            hiddenCreate: true,
            compositeKey: true,
        },
        note: {
            name: "Note",
            type: "string",
            trim: true,
            defaultValue: "",
            description: "Note for better management of mappings."
        },
        mapping: {
            name: "Mapping (URL fragment)",
            type: "string",
            trim: true,
            defaultValue: "/address/{id}",
            required: true,
            description: "You can use {id}, {id:digit}, {id:alpha}, {id:alnum}, {id:regex:[a-z]+}."
        },
        linkUrl: {
            name: "Linking (URL fragment)",
            type: "string",
            trim: true,
            defaultValue: "",
            description: "When mapping URL can't be used for linking, you can define Linking URL either here or with extra Mapping Definition. Leave empty to use Mapping URL."
        },
        action: {
            name: "Action",
            type: "enum",
            defaultValue: "handle",
            values: {
                "handle": {
                    name: "Handle as detail",
                },
                "list": {
                    name: "Handle as list",
                },
                "ignore-stop": {
                    name: "Ignore + stop processing",
                },
                "ignore": {
                    name: "Ignore + try to match better one",
                },
                "alias": {
                    name: "Alias to different URL",
                }
            }
        },
        alias: {
            name: "Alias to",
            type: "string",
            trim: true,
            defaultValue: "",
            visibility: (data) => { return data['action'] === 'alias'; }
        },
        workingStatus: {
            name: "Working status",
            type: "enum",
            defaultValue: WORKING_STATUS_OK,
            userEditable: false,
            values: {
                [WORKING_STATUS_OK]: {
                    name: "Ok",
                },
                [WORKING_STATUS_CACHING]: {
                    name: "Requires Caching",
                },
                [WORKING_STATUS_NO_ACTION]: {
                    name: "Missing action",
                },
            }
        },
        operation: {
            name: "Operation",
            type: "enum",
            defaultValue: "view",
            values: operationTypes,
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; }
        },
        subOperation: {
            name: "Sub operation",
            description: "Sub operation - if user is looking on a detail of item, we can name the sub operation. User can then change the view he is looking on.",
            type: "string",
            defaultValue: "",
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; }
        },
        fieldName: {
            name: "Field Name",
            type: "string",
            trim: true,
            defaultValue: "id",
            visibility: (data) => { return data['action'] !== 'ignore' && data['action'] !== 'ignore-stop' && data['mapping'] && data['mapping'].indexOf('{') !== -1; }
        },
        sourceDef: {
            name: "Source",
            type: "relation",
            to: () => types['sourceDefs'],
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; },
            relativeTo: ['channelDef'],
        },
        channelEntityDef: {
            name: "Channel Entity Def",
            type: "relation",
            to: () => types['channelEntityDefs'],
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; },
            relativeTo: ['channelDef'],
        },
        channelFieldDef: {
            name: "Channel Field Def",
            type: "relation",
            to: () => types['channelFieldDefs'],
            visibility: (data) => { return data['action'] === 'handle'; },
            relativeTo: ['channelDef', 'channelEntityDef'],
        },
        channelInjectionDef: {
            name: "Channel Injection Def",
            type: "relation",
            to: () => types['channelInjectionDefs'],
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; },
            relativeTo: ['channelDef'],
        },
        primary: {
            name: "Is Primary",
            type: "boolean",
            description: "Primary URLs are used for linking to service for current Source Definition",
            defaultValue: true,
            visibility: (data) => { return data['action'] === 'handle' || data['action'] === 'list'; }
        },
        order: {
            name: "Evaluate order",
            type: "int",
            defaultValue: 10,
        },
        conversion: {
            name: "Conversion",
            type: "object",
            defaultValue: [],
        },
        author: {
            name: "Author",
            type: "relation",
            to: () => types['users'],
            userEditable: false,
        },
        updatedAt: {
            name: "Updated At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        },
        createdAt: {
            name: "Created At",
            type: "datetime",
            defaultValue: () => {
                return new Date();
            },
            userEditable: false,
        }
    }; },
    relations: (types) => { return {
        mappingArgs: {
            type: 'oneToMany',
            to: types['channelMappingArgDefs'],
            export: true,
            defaultValue: () => {
                return {};
            },
        },
    }; },
});
